<template>
  <DataTable
    :key="offerSpin?.items.length"
    :value="offerSpin?.items"
    :reorderable-columns="!isSorting && isEditable"
    :loading="isSorting"
    table-style="margin-top: 1rem"
    :pt="{
      footer: {
        style: { padding: 0, border: isReady && 'none' },
      },
    }"
    @row-reorder="onRowReOrder"
  >
    <Column
      v-if="isEditable"
      row-reorder
      style="width: 5%"
      :reorderable-column="false"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
        root: {
          style: {
            borderLeft: '1px solid var(--vd-color-text-light-9) !important',
          },
        },
      }"
    />
    <Column
      header="Probability"
      style="width: 45%"
      :reorderable-column="false"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
        root: {
          style: {
            borderLeft: '1px solid var(--vd-color-text-light-9) !important',
          },
        },
      }"
    >
      <template #body="{ data }">
        <div :class="$style.probability">
          <VdIcon
            v-if="isEditable"
            store="fas"
            name="trash"
            :class="$style.deleteIcon"
            @click="() => deleteReward(data.id)"
          />
          <div :style="{ flex: 1 }" @click="(e) => toggleProbabilityPanel(e, data.id)">
            {{ data.probabilityPercentage }}%
          </div>
          <VdIcon v-if="isEditable" store="fa" name="chevron-down" size="small"></VdIcon>
        </div>
        <Popover ref="probabilityPanel">
          <div :class="$style.probabilityForm">
            <InputNumber
              v-model="probability"
              placeholder="Probability (%)"
              :class="$style.probabilityInput"
              :min="0"
              :max="100"
            />
            <Button label="Save" @click="handleProbabilityChange" />
          </div>
        </Popover>
      </template>
    </Column>
    <Column
      header="Rewards"
      style="width: 50%"
      :reorderable-column="false"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
      }"
    >
      <template #body="{ data }">
        <div
          :class="$style.rewardsWrapper"
          :style="{ cursor: isEditable ? 'pointer' : 'initial' }"
          @click="(e) => toggleRewardsPanel(e, data.id)"
        >
          <div :class="$style.rewardItemsWrapper">
            <div v-for="reward in data.items" :key="reward.id" :class="$style.rewardItem">
              <PriceItemWithQuantity :store-item="reward.item" :price-amount="reward.quantity" />
            </div>
          </div>
          <VdIcon v-if="isEditable" store="fa" name="chevron-down" size="small"></VdIcon>
        </div>

        <Popover ref="rewardsPanel">
          <ItemsBundlesList
            :reward-items="formatRewardItems(rewardItems)"
            :show-confirmation="false"
            @save="(items) => handleItemsSave(items)"
          />
        </Popover>
      </template>
    </Column>
    <template #footer>
      <WheelOfFortuneForm v-if="isEditable" @add-reward="addReward" />
    </template>
  </DataTable>
</template>

<script setup lang="ts">
import {
  OfferProductItem,
  OfferSpin,
  OfferSpinRewardInput,
  OfferSpinRewardUpdateInput,
  useCreateOfferSpinRewardMutation,
  useDeleteOfferSpinRewardMutation,
  useSortOfferSpinRewardsMutation,
  useUpdateOfferSpinRewardMutation,
} from "@/graphql";
import ItemsBundlesList from "@/modules/ItemsBundles";
import { FormattedItem } from "@/modules/Rewards/types";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputNumber from "primevue/inputnumber";
import Popover from "primevue/popover";
import { useConfirm } from "primevue/useconfirm";
import { PropType, onMounted, ref, watch } from "vue";

import { useRoute } from "vue-router";

import PriceItemWithQuantity from "../components/PriceItemWithQuantity.vue";
import WheelOfFortuneForm from "./WheelOfFortuneForm.vue";

const emit = defineEmits(["refetch-offer-spin", "is-offer-ready"]);

const props = defineProps({
  offerId: {
    type: String,
    required: true,
  },
  offerSpin: {
    type: Object as PropType<OfferSpin | null>,
  },
  isEditable: {
    type: Boolean,
    required: true,
  },
});

const confirm = useConfirm();
const route = useRoute();
const appId = ref<string>(route.params.gameId as string);
const versionId = ref<string>(route.params.versionId as string);

const selectedReward = ref("");
const rewardsPanel = ref<InstanceType<typeof Popover> | null>(null);
const probabilityPanel = ref<InstanceType<typeof Popover> | null>(null);
const probability = ref(0);
const rewardItems = ref<OfferProductItem[]>([]);

const apiRequestsPayload = {
  appId: appId.value,
  version: versionId.value,
  offerId: props.offerId,
};

const { mutate: createOfferSpinReward } = useCreateOfferSpinRewardMutation({});
const { mutate: deleteOfferSpinReward } = useDeleteOfferSpinRewardMutation({});
const { mutate: updateOfferSpinReward } = useUpdateOfferSpinRewardMutation({});
const { mutate: sortOfferSpinRewards, loading: isSorting } = useSortOfferSpinRewardsMutation({});

const toggleProbabilityPanel = (event: MouseEvent, offerSpinRewardId: string) => {
  const offerSpinToEdit = props.offerSpin?.items.find((offer) => offer && offer.id === offerSpinRewardId);

  if (offerSpinToEdit) {
    probability.value = offerSpinToEdit.probabilityPercentage;
  }

  selectedReward.value = offerSpinRewardId;

  if (!props.isEditable) {
    return;
  }

  probabilityPanel.value?.toggle(event);
};

const toggleRewardsPanel = (event: MouseEvent, offerSpinRewardId: string) => {
  const offerSpinToEdit = props.offerSpin?.items.find((offer) => offer && offer.id === offerSpinRewardId);

  if (offerSpinToEdit) {
    rewardItems.value = offerSpinToEdit.items;
  }

  selectedReward.value = offerSpinRewardId;

  if (!props.isEditable) {
    return;
  }

  rewardsPanel.value?.toggle(event);
};

const formatRewardItems = (items: OfferProductItem[]) =>
  items?.map((item) => ({
    selectedItem: {
      id: item.itemId || "",
      name: item.item?.name || "",
      sprite: {
        imageUrl: item.item?.sprite?.imageUrl || "",
        name: item.item?.sprite?.name || "",
        id: item.item?.sprite?.id || "",
      },
    },
    quantity: item.quantity,
  }));

const updateReward = async (offerSpinRewardId: string, input: OfferSpinRewardUpdateInput) => {
  await updateOfferSpinReward({
    ...apiRequestsPayload,
    offerId: props.offerId,
    offerSpinId: props.offerSpin?.id || "",
    offerSpinRewardId,
    input: {
      rank: input.rank || 1,
      probabilityPercentage: input.probabilityPercentage,
      items: input.items.map((item) => ({
        itemId: item.itemId,
        quantity: item.quantity,
      })),
      id: input.id,
    },
  });
};

const addReward = async (product: OfferSpinRewardInput) => {
  const input: OfferSpinRewardInput = {
    ...product,
    rank: (props.offerSpin?.items && props.offerSpin.items.length + 1) || 1,
  };

  await createOfferSpinReward({
    ...apiRequestsPayload,
    offerId: props.offerId,
    offerSpinId: props.offerSpin?.id || "",
    input,
  });

  emit("refetch-offer-spin");
};

const deleteReward = async (offerSpinRewardId: string) => {
  confirm.require({
    message: "Are you sure want to delete this reward?",
    header: "Confirmation",
    rejectClass: "p-button-secondary p-button-outlined",
    acceptClass: "p-button-danger",
    acceptIcon: "pi pi-trash",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    accept: async () => {
      try {
        await deleteOfferSpinReward({
          ...apiRequestsPayload,
          offerId: props.offerId,
          offerSpinId: props.offerSpin?.id || "",
          offerSpinRewardId,
        });

        emit("refetch-offer-spin");
      } catch (error) {
        console.log(error, "error");
      }
    },
  });
};

const handleProbabilityChange = async () => {
  const originalOffer = props.offerSpin?.items.find((offer) => offer && offer.id === selectedReward.value);
  if (!originalOffer) {
    return;
  }

  const payload = { ...originalOffer };
  payload.probabilityPercentage = probability.value;

  await updateReward(originalOffer.id, payload);

  probabilityPanel.value?.hide();
};

const handleItemsSave = async (items: FormattedItem[]) => {
  const originalOffer = props.offerSpin?.items.find((offer) => offer && offer.id === selectedReward.value);

  if (!originalOffer) {
    return;
  }

  const payload = { ...originalOffer };

  payload.items = items.map((item) => ({
    itemId: item.selectedItem?.id || "",
    quantity: item.quantity,
  })) as OfferProductItem[];

  await updateReward(selectedReward.value, payload);

  rewardsPanel.value?.hide();
};

async function onRowReOrder(event: { dragIndex: number; dropIndex: number; value: OfferSpin[] }) {
  // change the ranking of the element in the array based on the dragIndex
  const sortingPayload = event.value.map((offerProduct, index) => ({
    id: offerProduct.id,
    rank: index + 1,
  }));

  await sortOfferSpinRewards({
    input: sortingPayload,
    offerId: props.offerId,
    offerSpinId: props.offerSpin?.id || "",
    appId: appId.value,
    version: versionId.value,
  });

  emit("refetch-offer-spin");
}

const isReady = ref(false);

watch(
  () => props.offerSpin?.items,
  (products) => {
    const totalProbability = products?.reduce((acc, product) => acc + product.probabilityPercentage, 0);
    const hasTwoItems = products && products.length > 1;
    isReady.value = (totalProbability === 100 && hasTwoItems) || false;
    emit("is-offer-ready", totalProbability === 100 && hasTwoItems);
  }
);

onMounted(() => {
  const totalProbability = props.offerSpin?.items.reduce((acc, product) => acc + product.probabilityPercentage, 0);
  const hasTwoItems = props.offerSpin?.items && props.offerSpin?.items.length > 1;
  isReady.value = (totalProbability === 100 && hasTwoItems) || false;
  emit("is-offer-ready", totalProbability === 100 && hasTwoItems);
});
</script>

<style module lang="scss">
.theadStyle {
  background-color: var(--vd-color-text-light-9) !important;
  border: 2px solid var(--vd-color-text-light-9) !important;
}

.rewardsWrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .rewardItemsWrapper {
    width: 100%;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  svg {
    color: var(--vd-color-text-light-2);
  }
}

.rewardItem {
  display: flex;
  align-items: center;
}

.deleteIcon {
  cursor: pointer;
  font-size: 12px;
  color: var(--vd-color-text-light-2);
  margin-right: 1rem;
}

.probability {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.probabilityForm {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>

<style scoped>
:deep(.p-datatable-table-container) {
  overflow: initial !important;
}
</style>
